import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FaArrowLeft, FaHeart, FaMoon, FaSun, FaCloud, FaCheckCircle, FaCog, FaCalendarAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'sonner';
import PHASE_INFO from '../data/hormonalCyclePhases';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SYMPTOMS = [
  'Cólicos', 'Cansancio', 'Retención de líquidos', 'Más hambre',
  'Ansiedad / cravings', 'Sensibilidad en senos', 'Cambios de ánimo',
  'Dolor de cabeza', 'Sueño alterado', 'Inflamación abdominal', 'Bajo rendimiento'
];

const CHECKIN_FIELDS = [
  { key: 'energy', label: 'Energía' },
  { key: 'hunger', label: 'Hambre' },
  { key: 'cravings', label: 'Cravings' },
  { key: 'bloating', label: 'Inflamación / retención' },
  { key: 'sleep', label: 'Sueño' },
  { key: 'menstrual_pain', label: 'Dolor menstrual' },
  { key: 'workout_motivation', label: 'Motivación para entrenar' },
  { key: 'perceived_performance', label: 'Rendimiento percibido' },
  { key: 'mood', label: 'Estado de ánimo' }
];

const RatingScale = ({ value, onChange, testId }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${
          value === n ? 'bg-pink-500 text-white' : 'bg-white/5 text-gray-500 hover:bg-white/10'
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

export default function MyHormonalCycle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [view, setView] = useState('welcome'); // welcome | form | dashboard | settings
  const [form, setForm] = useState({
    last_period_start_date: '',
    average_cycle_length: '25-28',
    average_bleeding_days: '4-5',
    cycle_regular_status: 'regular',
    hormonal_contraceptive_status: 'no',
    symptoms: []
  });
  const [checkin, setCheckin] = useState({});
  const [savingCheckin, setSavingCheckin] = useState(false);
  // Calendario de periodos (estilo Flo)
  const [periods, setPeriods] = useState([]);
  const [newPeriod, setNewPeriod] = useState({ start_date: '', end_date: '', flow: 'medium' });
  // Síntomas del día actual
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dailySymptoms, setDailySymptoms] = useState({ date: todayStr, symptoms: [], mood: null, notes: '' });

  const authHeaders = () => {
    const token = localStorage.getItem('deisycfit_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/hormonal-cycle/status`, { headers: authHeaders() });
      setStatus(res.data);
      if (res.data.enabled && res.data.last_period_start_date) {
        setView('dashboard');
        // Cargar periodos y síntomas de hoy
        try {
          const [pRes, sRes] = await Promise.all([
            axios.get(`${API}/hormonal-cycle/period-log`, { headers: authHeaders() }),
            axios.get(`${API}/hormonal-cycle/daily-symptoms?limit=1`, { headers: authHeaders() })
          ]);
          setPeriods(pRes.data?.periods || []);
          const todayLog = (sRes.data?.daily_symptoms || []).find(d => d.date === todayStr);
          if (todayLog) {
            setDailySymptoms({
              date: todayStr,
              symptoms: todayLog.symptoms || [],
              mood: todayLog.mood || null,
              notes: todayLog.notes || ''
            });
          }
        } catch (e) { /* silent */ }
      } else {
        setView('welcome');
      }
    } catch (e) {
      console.error('Error loading hormonal cycle status:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPeriod = async () => {
    if (!newPeriod.start_date) {
      toast.error('Selecciona la fecha de inicio');
      return;
    }
    try {
      await axios.post(`${API}/hormonal-cycle/period-log`, newPeriod, { headers: authHeaders() });
      toast.success('Fecha de periodo registrada');
      setNewPeriod({ start_date: '', end_date: '', flow: 'medium' });
      await fetchStatus();
    } catch (e) {
      toast.error('No pudimos registrar la fecha');
    }
  };

  const handleDeletePeriod = async (id) => {
    if (!window.confirm('¿Eliminar este registro de periodo?')) return;
    try {
      await axios.delete(`${API}/hormonal-cycle/period-log/${id}`, { headers: authHeaders() });
      toast.success('Registro eliminado');
      await fetchStatus();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const toggleDailySymptom = (s) => {
    setDailySymptoms(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(s)
        ? prev.symptoms.filter(x => x !== s)
        : [...prev.symptoms, s]
    }));
  };

  const handleSaveDailySymptoms = async () => {
    try {
      await axios.post(`${API}/hormonal-cycle/daily-symptoms`, dailySymptoms, { headers: authHeaders() });
      toast.success('Síntomas de hoy guardados');
    } catch (e) {
      toast.error('Error al guardar síntomas');
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const toggleSymptom = (s) => {
    setForm((p) => ({
      ...p,
      symptoms: p.symptoms.includes(s) ? p.symptoms.filter((x) => x !== s) : [...p.symptoms, s]
    }));
  };

  const handleSetup = async () => {
    if (!form.last_period_start_date) {
      toast.error('Selecciona la fecha de inicio del último periodo');
      return;
    }
    try {
      await axios.post(`${API}/hormonal-cycle/setup`, form, { headers: authHeaders() });
      toast.success('Datos guardados. Aquí tienes tus sugerencias.');
      await fetchStatus();
      setView('dashboard');
    } catch (e) {
      toast.error('No pudimos guardar tus datos');
      console.error('Cycle setup error:', e?.response || e);
    }
  };

  const handleSubmitCheckin = async () => {
    setSavingCheckin(true);
    try {
      await axios.post(`${API}/hormonal-cycle/checkin`, checkin, { headers: authHeaders() });
      toast.success('Check-in registrado');
      setCheckin({});
    } catch (e) {
      toast.error('Error al guardar el check-in');
    } finally {
      setSavingCheckin(false);
    }
  };

  const handleDismissBanner = async (newStatus) => {
    try {
      await axios.put(`${API}/hormonal-cycle/banner-status`, { status: newStatus }, { headers: authHeaders() });
    } catch (e) { /* silent */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-16 h-16 rounded-full animate-pulse" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} />
      </div>
    );
  }

  // ============== WELCOME SCREEN ==============
  if (view === 'welcome') {
    return (
      <div className="min-h-screen px-6 py-10" style={{ backgroundColor: '#0a0a0a' }} data-testid="hormonal-welcome-screen">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6 text-gray-400" data-testid="back-button">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.1), rgba(192, 132, 252, 0.05))', border: '1px solid rgba(192, 132, 252, 0.2)' }}>
            <div className="text-6xl mb-4">🌙</div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Mi ciclo hormonal
            </h1>
            <p className="text-gray-300 mb-2 text-base leading-relaxed">
              Tu ciclo puede influir en tu energía, hambre, recuperación, sueño y rendimiento.
            </p>
            <p className="text-gray-400 text-sm mb-8 leading-relaxed">
              Esta herramienta no cambia automáticamente tu plan, pero te dará sugerencias opcionales para entender mejor cómo te sientes durante el mes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
              <Button onClick={() => setView('form')} className="text-white font-semibold px-6 py-5 rounded-full" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} data-testid="start-cycle-setup-btn">
                Empezar registro
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-white/20 text-gray-300 hover:bg-white/5" data-testid="skip-cycle-btn">
                Omitir por ahora
              </Button>
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await axios.put(`${API}/hormonal-cycle/banner-status`, { status: 'hidden_permanently' }, { headers: authHeaders() });
                  await axios.put(`${API}/hormonal-cycle/reminders`, {
                    menstrual_start: false,
                    luteal_phase: false,
                    ovulation: false,
                    daily_checkin: false,
                    hide_all: true
                  }, { headers: authHeaders() });
                } catch (e) { /* silent */ }
                toast.success('Listo. No volveremos a mostrarte esta herramienta.');
                navigate('/dashboard');
              }}
              className="mt-5 text-xs text-gray-500 hover:text-gray-300 underline-offset-4 hover:underline"
              data-testid="no-menstruo-btn"
            >
              No menstrúo o no me interesa esta herramienta
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ============== FORM SCREEN ==============
  if (view === 'form') {
    return (
      <div className="min-h-screen px-6 py-10" style={{ backgroundColor: '#0a0a0a' }} data-testid="hormonal-form-screen">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => setView('welcome')} className="mb-6 text-gray-400">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <h2 className="text-2xl font-bold text-white mb-1">Cuéntanos sobre tu ciclo</h2>
            <p className="text-gray-400 text-sm mb-6">Esta información es privada y solo se usa para mostrarte sugerencias.</p>

            <div className="space-y-5">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Fecha de inicio del último periodo</label>
                <Input type="date" value={form.last_period_start_date} onChange={(e) => setForm({ ...form, last_period_start_date: e.target.value })} className="bg-white/5 border-white/10 text-white" data-testid="last-period-input" />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Duración promedio del ciclo</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {[
                    { v: '21-24', l: '21–24 días' },
                    { v: '25-28', l: '25–28 días' },
                    { v: '29-32', l: '29–32 días' },
                    { v: '33+', l: '33+ días' },
                    { v: 'no_segura', l: 'No estoy segura' }
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setForm({ ...form, average_cycle_length: o.v })}
                      className={`text-xs px-3 py-2 rounded-lg transition-all ${form.average_cycle_length === o.v ? 'bg-pink-500/30 text-white border border-pink-500' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
                      data-testid={`cycle-length-${o.v}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Duración promedio del sangrado</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { v: '2-3', l: '2–3 días' },
                    { v: '4-5', l: '4–5 días' },
                    { v: '6-7', l: '6–7 días' },
                    { v: 'more_7', l: 'Más de 7 días' }
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setForm({ ...form, average_bleeding_days: o.v })}
                      className={`text-xs px-3 py-2 rounded-lg transition-all ${form.average_bleeding_days === o.v ? 'bg-pink-500/30 text-white border border-pink-500' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">¿Tu ciclo es regular o irregular?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'regular', l: 'Regular' },
                    { v: 'irregular', l: 'Irregular' },
                    { v: 'no_segura', l: 'No estoy segura' }
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setForm({ ...form, cycle_regular_status: o.v })}
                      className={`text-xs px-3 py-2 rounded-lg transition-all ${form.cycle_regular_status === o.v ? 'bg-pink-500/30 text-white border border-pink-500' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">¿Usas anticonceptivos hormonales?</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'si', l: 'Sí' },
                    { v: 'no', l: 'No' },
                    { v: 'prefer_no_answer', l: 'Prefiero no responder' }
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setForm({ ...form, hormonal_contraceptive_status: o.v })}
                      className={`text-xs px-3 py-2 rounded-lg transition-all ${form.hormonal_contraceptive_status === o.v ? 'bg-pink-500/30 text-white border border-pink-500' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Síntomas frecuentes</label>
                <div className="flex flex-wrap gap-2">
                  {SYMPTOMS.map((s) => (
                    <button key={s} type="button" onClick={() => toggleSymptom(s)}
                      className={`text-xs px-3 py-2 rounded-full transition-all ${form.symptoms.includes(s) ? 'bg-purple-500/30 text-white border border-purple-500' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
                      data-testid={`symptom-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={handleSetup} className="w-full text-white font-semibold py-6 rounded-full" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} data-testid="save-cycle-setup-btn">
                Guardar y ver sugerencias
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== DASHBOARD VIEW ==============
  const phase = status?.current_estimated_phase;
  const phaseData = phase ? PHASE_INFO[phase] : null;
  const phaseInfo = status?.phase_info;
  const isIrregular = status?.cycle_regular_status === 'irregular';

  return (
    <div className="min-h-screen px-6 py-8" style={{ backgroundColor: '#0a0a0a' }} data-testid="hormonal-dashboard">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-gray-400">
            <FaArrowLeft className="mr-2" /> Volver
          </Button>
          <Button variant="ghost" onClick={() => setView('form')} className="text-pink-400" data-testid="edit-cycle-btn">
            <FaCog className="mr-2" /> Editar datos
          </Button>
        </div>

        {/* Phase Card */}
        {phaseData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-6 md:p-8 mb-6"
            style={{ background: `linear-gradient(135deg, ${phaseData.color}25, ${phaseData.color}05)`, border: `1px solid ${phaseData.color}40` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="text-4xl">{phaseData.emoji}</div>
              <div>
                <p className="text-xs uppercase tracking-wider" style={{ color: phaseData.color }}>Fase estimada</p>
                <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="current-phase-name">{phaseData.name}</h1>
              </div>
            </div>
            {phaseInfo && (
              <p className="text-xs text-gray-400 mb-4">
                Día {phaseInfo.day_in_cycle} de tu ciclo ({phaseInfo.cycle_length_days} días). Próximo periodo aprox. en {phaseInfo.days_until_next_period} días.
              </p>
            )}
            {isIrregular && (
              <p className="text-xs text-amber-400 mb-4">
                ⚠️ Esta es una estimación basada en la información registrada. Tu ciclo es irregular, así que puede variar.
              </p>
            )}
            <p className="text-gray-300 text-sm leading-relaxed">{phaseData.description}</p>
          </motion.div>
        )}

        {/* 4 Suggestion Blocks */}
        {phaseData && (
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <SuggestionCard icon={<FaHeart />} title="Qué podrías sentir" color="var(--brand-primary)" items={phaseData.feelings} testId="suggestions-feelings" />
            <SuggestionCard icon={<FaSun />} title="Nutrición sugerida" color="#00F0FF" items={phaseData.nutrition} testId="suggestions-nutrition" />
            <SuggestionCard icon={<FaCloud />} title="Entrenamiento sugerido" color="#CCFF00" items={phaseData.training} testId="suggestions-training" />
            <SuggestionCard icon={<FaMoon />} title="Recuperación / autocuidado" color="#C084FC" items={phaseData.recovery} testId="suggestions-recovery" />
          </div>
        )}

        {/* Calendario de periodos (estilo Flo) — editar fechas pasadas y agregar nuevas */}
        <div className="rounded-2xl p-6 mb-6" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }} data-testid="period-log-panel">
          <div className="flex items-center gap-2 mb-1">
            <FaCalendarAlt className="text-pink-400" />
            <h3 className="text-lg font-bold text-white">Mis periodos</h3>
          </div>
          <p className="text-xs text-gray-400 mb-4">Cada mes registra cuándo te vino. La fase y predicciones se ajustan automáticamente.</p>

          {/* Agregar nuevo periodo */}
          <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(var(--brand-primary-glow), 0.06)', border: '1px solid rgba(var(--brand-primary-glow), 0.2)' }}>
            <p className="text-xs text-pink-300 font-semibold mb-2">➕ Registrar nuevo periodo</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Inicio</label>
                <Input type="date" value={newPeriod.start_date} onChange={(e) => setNewPeriod({ ...newPeriod, start_date: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm h-9" data-testid="new-period-start" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Fin (opcional)</label>
                <Input type="date" value={newPeriod.end_date} onChange={(e) => setNewPeriod({ ...newPeriod, end_date: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm h-9" />
              </div>
            </div>
            <div className="flex gap-2 mb-3">
              {[
                { v: 'light', l: '💧 Ligero' },
                { v: 'medium', l: '💧💧 Medio' },
                { v: 'heavy', l: '💧💧💧 Abundante' }
              ].map(f => (
                <button key={f.v} type="button" onClick={() => setNewPeriod({ ...newPeriod, flow: f.v })}
                  className={`text-xs px-2 py-1.5 rounded-lg flex-1 transition-all ${newPeriod.flow === f.v ? 'bg-pink-500/30 text-white' : 'bg-white/5 text-gray-400'}`}>
                  {f.l}
                </button>
              ))}
            </div>
            <Button onClick={handleAddPeriod} size="sm" className="w-full text-white" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} data-testid="save-new-period-btn">
              <FaPlus className="mr-2" /> Guardar
            </Button>
          </div>

          {/* Lista de periodos registrados */}
          {periods.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {periods.map(p => (
                <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-white/5" data-testid={`period-row-${p.id}`}>
                  <div>
                    <p className="text-white text-sm font-semibold">{p.start_date} {p.end_date && `→ ${p.end_date}`}</p>
                    {p.flow && <p className="text-xs text-gray-400">{p.flow === 'light' ? '💧 Ligero' : p.flow === 'medium' ? '💧💧 Medio' : '💧💧💧 Abundante'}</p>}
                  </div>
                  <button onClick={() => handleDeletePeriod(p.id)} className="text-gray-500 hover:text-red-400 p-2" data-testid={`delete-period-${p.id}`}>
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-4">Aún no has registrado periodos.</p>
          )}
        </div>

        {/* Síntomas y ánimo de HOY */}
        <div className="rounded-2xl p-6 mb-6" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }} data-testid="daily-symptoms-panel">
          <h3 className="text-lg font-bold text-white mb-1">¿Cómo te sientes hoy?</h3>
          <p className="text-xs text-gray-400 mb-4">Marca los síntomas que tienes hoy y tu ánimo. Ayuda a entender tu patrón.</p>

          {/* Mood */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2">Estado de ánimo</p>
            <div className="flex flex-wrap gap-2">
              {[
                { v: 'feliz', l: '😊 Feliz' },
                { v: 'calmada', l: '😌 Calmada' },
                { v: 'energica', l: '⚡ Enérgica' },
                { v: 'cansada', l: '😴 Cansada' },
                { v: 'triste', l: '😔 Triste' },
                { v: 'ansiosa', l: '😟 Ansiosa' },
                { v: 'irritable', l: '😤 Irritable' }
              ].map(m => (
                <button key={m.v} type="button"
                  onClick={() => setDailySymptoms({ ...dailySymptoms, mood: m.v })}
                  className={`text-xs px-3 py-2 rounded-full transition-all ${dailySymptoms.mood === m.v ? 'bg-purple-500/30 text-white border border-purple-500' : 'bg-white/5 text-gray-400 border border-white/10'}`}
                  data-testid={`mood-${m.v}`}>
                  {m.l}
                </button>
              ))}
            </div>
          </div>

          {/* Symptoms */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2">Síntomas de hoy</p>
            <div className="flex flex-wrap gap-2">
              {SYMPTOMS.map(s => (
                <button key={s} type="button" onClick={() => toggleDailySymptom(s)}
                  className={`text-xs px-3 py-2 rounded-full transition-all ${dailySymptoms.symptoms.includes(s) ? 'bg-pink-500/30 text-white border border-pink-500' : 'bg-white/5 text-gray-400 border border-white/10'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <Textarea placeholder="Notas del día (opcional)" value={dailySymptoms.notes} onChange={(e) => setDailySymptoms({ ...dailySymptoms, notes: e.target.value })} className="bg-white/5 border-white/10 text-white text-sm mb-3" />
          <Button onClick={handleSaveDailySymptoms} className="w-full text-white" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} data-testid="save-daily-symptoms-btn">
            <FaCheckCircle className="mr-2" /> Guardar día
          </Button>
        </div>

        {/* Daily Check-in */}
        <div className="rounded-2xl p-6 mb-6" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <h3 className="text-lg font-bold text-white mb-1">Check-in de hoy (opcional)</h3>
          <p className="text-xs text-gray-400 mb-4">Anota cómo te sientes hoy. No es obligatorio.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {CHECKIN_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-3 py-2">
                <label className="text-sm text-gray-300 flex-1">{f.label}</label>
                <RatingScale value={checkin[f.key]} onChange={(v) => setCheckin({ ...checkin, [f.key]: v })} testId={`rating-${f.key}`} />
              </div>
            ))}
          </div>
          <Textarea placeholder="Notas (opcional)" value={checkin.notes || ''} onChange={(e) => setCheckin({ ...checkin, notes: e.target.value })} className="mt-3 bg-white/5 border-white/10 text-white text-sm" />
          <Button onClick={handleSubmitCheckin} disabled={savingCheckin || Object.keys(checkin).length === 0} className="mt-4 w-full text-white" style={{ background: 'linear-gradient(135deg, var(--brand-primary), #C084FC)' }} data-testid="submit-checkin-btn">
            <FaCheckCircle className="mr-2" /> {savingCheckin ? 'Guardando...' : 'Guardar check-in'}
          </Button>
        </div>

        <p className="text-xs text-center text-gray-600 mb-4">
          Las fases son estimadas, no diagnósticas. Esta herramienta no reemplaza consulta médica.
        </p>
      </div>
    </div>
  );
}

const SuggestionCard = ({ icon, title, color, items, testId }) => (
  <div className="rounded-xl p-4" style={{ background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <span style={{ color }}>{icon}</span>
      <h4 className="font-bold text-white text-sm uppercase tracking-wider">{title}</h4>
    </div>
    <ul className="space-y-2">
      {(items || []).map((item, i) => (
        <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
          <span style={{ color }} className="mt-1">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);
